/**
 * useReports — REQ-090 state machine for the admin Operational Reports &
 * Export module (ODE-P07-T09).
 *
 * Two independent guarded flows:
 *
 *   read (`GET /v1/admin/reports`, `reports.view`)
 *     idle → submit() → loading → the guarded report outcome:
 *       200 with rows → `ready`
 *       200 with 0 rows → `empty` (explicit "no activity in this range")
 *       400 → `error`   (rejected input — retryable with corrected filters)
 *       401 → `unauthorized`
 *       403 → `blocked` (server-enforced; the client mirror is UX only)
 *       503/network → `error`
 *
 *   export (`GET /v1/admin/reports/export`, reused `reports.financial.export`)
 *     idle → export() → loading →
 *       200 → `ready` (the exact server bytes + REQ-076 output hash)
 *       401 → `unauthorized`
 *       403 `EXPORT_NOT_AUTHORIZED` → `not_authorized` (REQ-088 owner gate)
 *       403 other → `blocked` (role / missing purpose code)
 *       400 → `error`, 503/network → `error`
 *
 * Nothing is computed here: every number, the total, the sort key, the cursor
 * and the export bytes come from the server projection verbatim. The client
 * never slices, re-pages, re-derives or re-serializes a report.
 *
 * Traces to: ODE-PRD-REQ-048, ODE-PRD-REQ-077, ODE-PRD-REQ-088,
 *            ODE-PRD-REQ-090.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  type ReportExportResult,
  type ReportsFetchResult,
  type ReportsPage,
  type ReportsQuery,
} from '../../api/client.js';

/** The fixed report kinds served by the API (REQ-048). */
export const REPORT_KINDS = ['recap', 'do_rollup', 'driver_vehicle_rollup'] as const;
export type ReportsKind = (typeof REPORT_KINDS)[number];

/** Plain-Indonesian labels for the fixed report kinds. */
export const REPORT_KIND_LABELS: Readonly<Record<ReportsKind, string>> = {
  recap: 'Rekap harian',
  do_rollup: 'Rollup per Delivery Order',
  driver_vehicle_rollup: 'Rollup per driver/kendaraan',
};

/** The fixed export formats (stream-only, REQ-048). */
export const REPORT_EXPORT_FORMATS = ['csv', 'json'] as const;
export type ReportExportFormat = (typeof REPORT_EXPORT_FORMATS)[number];

/** Client mirror of the server bounds (never a silent clamp). */
export const REPORTS_LIMIT_MIN = 1;
export const REPORTS_LIMIT_MAX = 500;
export const REPORTS_LIMIT_DEFAULT = 100;
export const REPORTS_MAX_RANGE_DAYS = 366;
export const REPORTS_CURSOR_MAX_LENGTH = 512;
export const REPORT_DO_ID_PATTERN =
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
export const REPORT_OPERATIONAL_ID_PATTERN = /^[A-Za-z0-9._-]{1,64}$/;
export const REPORT_DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const MILLIS_PER_DAY = 86_400_000;

export interface ReportsForm {
  readonly kind: ReportsKind;
  readonly from: string;
  readonly to: string;
  readonly driverId: string;
  readonly vehicleId: string;
  readonly doId: string;
  readonly limit: string;
}

export const EMPTY_REPORTS_FORM: ReportsForm = {
  kind: 'recap',
  from: '',
  to: '',
  driverId: '',
  vehicleId: '',
  doId: '',
  limit: String(REPORTS_LIMIT_DEFAULT),
};

export interface ReportsFormErrors {
  readonly kind?: string;
  readonly from?: string;
  readonly to?: string;
  readonly range?: string;
  readonly driverId?: string;
  readonly vehicleId?: string;
  readonly doId?: string;
  readonly limit?: string;
}

/** Parses `YYYY-MM-DD` into inclusive UTC start/end epoch ms (null if invalid). */
export function dayBounds(day: string): { readonly startMs: number; readonly endMs: number } | null {
  if (!REPORT_DAY_PATTERN.test(day)) return null;
  const startMs = Date.parse(`${day}T00:00:00.000Z`);
  if (!Number.isFinite(startMs)) return null;
  if (new Date(startMs).toISOString().slice(0, 10) !== day) return null;
  return { startMs, endMs: startMs + MILLIS_PER_DAY - 1 };
}

/**
 * Pure fail-closed form validation. Mirrors the server bounds exactly: the
 * client refuses to submit anything it knows the server would reject, and it
 * never drops, coerces or clamps a filter.
 */
export function validateReportsForm(form: ReportsForm): ReportsFormErrors {
  const errors: Record<string, string> = {};

  if (!(REPORT_KINDS as readonly string[]).includes(form.kind)) {
    errors.kind = 'Jenis laporan tidak dikenal.';
  }

  let fromBounds: { startMs: number; endMs: number } | null = null;
  let toBounds: { startMs: number; endMs: number } | null = null;
  if (form.from.trim() !== '') {
    fromBounds = dayBounds(form.from.trim());
    if (fromBounds === null) errors.from = 'Tanggal awal harus format YYYY-MM-DD (UTC).';
  }
  if (form.to.trim() !== '') {
    toBounds = dayBounds(form.to.trim());
    if (toBounds === null) errors.to = 'Tanggal akhir harus format YYYY-MM-DD (UTC).';
  }
  if (fromBounds !== null && toBounds !== null) {
    if (fromBounds.startMs > toBounds.endMs) {
      errors.range = 'Tanggal awal tidak boleh melewati tanggal akhir.';
    } else {
      const spanDays = Math.floor((toBounds.endMs - fromBounds.startMs) / MILLIS_PER_DAY) + 1;
      if (spanDays > REPORTS_MAX_RANGE_DAYS) {
        errors.range = `Rentang tanggal tidak boleh lebih dari ${REPORTS_MAX_RANGE_DAYS} hari.`;
      }
    }
  }

  if (form.driverId.trim() !== '' && !REPORT_OPERATIONAL_ID_PATTERN.test(form.driverId.trim())) {
    errors.driverId = 'ID driver tidak valid (hanya huruf/angka/._-).';
  }
  if (form.vehicleId.trim() !== '' && !REPORT_OPERATIONAL_ID_PATTERN.test(form.vehicleId.trim())) {
    errors.vehicleId = 'ID kendaraan tidak valid (hanya huruf/angka/._-).';
  }
  if (form.doId.trim() !== '' && !REPORT_DO_ID_PATTERN.test(form.doId.trim())) {
    errors.doId = 'ID Delivery Order harus UUID.';
  }

  const limitRaw = form.limit.trim();
  if (limitRaw === '' || !/^\d+$/.test(limitRaw)) {
    errors.limit = 'Batas baris harus bilangan bulat positif.';
  } else {
    const limit = Number(limitRaw);
    if (limit < REPORTS_LIMIT_MIN || limit > REPORTS_LIMIT_MAX) {
      errors.limit = `Batas baris harus antara ${REPORTS_LIMIT_MIN} dan ${REPORTS_LIMIT_MAX}.`;
    }
  }

  return errors;
}

export function hasReportsFormErrors(errors: ReportsFormErrors): boolean {
  return Object.values(errors).some((value) => value !== undefined);
}

/**
 * Pure query encoder: only validated, bounded values reach the wire. An empty
 * filter is omitted (never sent as an empty string) so the server treats it as
 * "unbounded", exactly like the SPA shows it.
 */
export function toReportsQuery(form: ReportsForm, cursor: string | null = null): ReportsQuery {
  const query: {
    kind: ReportsKind;
    from?: string;
    to?: string;
    driverId?: string;
    vehicleId?: string;
    doId?: string;
    limit?: number;
    cursor?: string;
  } = { kind: form.kind, limit: Number(form.limit.trim()) };
  if (form.from.trim() !== '') query.from = form.from.trim();
  if (form.to.trim() !== '') query.to = form.to.trim();
  if (form.driverId.trim() !== '') query.driverId = form.driverId.trim();
  if (form.vehicleId.trim() !== '') query.vehicleId = form.vehicleId.trim();
  if (form.doId.trim() !== '') query.doId = form.doId.trim();
  if (cursor !== null && cursor !== '') query.cursor = cursor;
  return query;
}

export type ReportsState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly page: ReportsPage }
  | { readonly kind: 'empty'; readonly page: ReportsPage }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export type ReportExportState =
  | { readonly kind: 'idle' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly result: ReportExportResult }
  | { readonly kind: 'unauthorized' }
  | { readonly kind: 'not_authorized'; readonly message: string }
  | { readonly kind: 'blocked'; readonly message: string }
  | { readonly kind: 'error'; readonly message: string };

export interface UseReportsOptions {
  readonly token: string | null;
  /** Test seam: report fetch (default: the shared api client). */
  readonly fetch?: ((query: ReportsQuery, token: string) => Promise<ReportsFetchResult>) | undefined;
  /** Test seam: export fetch (default: the shared api client). */
  readonly fetchExport?:
    | ((
        request: { query: ReportsQuery; format: ReportExportFormat; purposeCode: string },
        token: string,
      ) => Promise<ReportExportResult>)
    | undefined;
}

export interface UseReportsResult {
  readonly form: ReportsForm;
  readonly setField: (field: keyof ReportsForm, value: string) => void;
  readonly errors: ReportsFormErrors;
  readonly submit: () => void;
  readonly nextPage: () => void;
  readonly reset: () => void;
  readonly state: ReportsState;
  readonly format: ReportExportFormat;
  readonly setFormat: (value: ReportExportFormat) => void;
  readonly purposeCode: string;
  readonly setPurposeCode: (value: string) => void;
  readonly runExport: () => void;
  readonly exportState: ReportExportState;
  readonly resetExport: () => void;
}

function mapReportsResult(result: ReportsFetchResult): ReportsState {
  switch (result.status) {
    case 200:
      if (result.payload === null) {
        return { kind: 'error', message: 'Respons laporan tidak valid.' };
      }
      return result.payload.rows.length === 0
        ? { kind: 'empty', page: result.payload }
        : { kind: 'ready', page: result.payload };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      return { kind: 'blocked', message: result.message ?? 'Akses ditolak.' };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}

function mapExportResult(result: ReportExportResult): ReportExportState {
  switch (result.status) {
    case 200:
      if (result.body === null) {
        return { kind: 'error', message: 'Respons ekspor tidak valid.' };
      }
      return { kind: 'ready', result };
    case 401:
      return { kind: 'unauthorized' };
    case 403:
      // REQ-088: an owner-policy gate is NOT a role denial — surface it
      // distinctly so the operator knows the decision is still open.
      return result.errorCode === 'EXPORT_NOT_AUTHORIZED'
        ? {
            kind: 'not_authorized',
            message:
              result.message ??
              'Ekspor belum diizinkan: kebijakan ekspor pemilik belum disetujui (REQ-088).',
          }
        : { kind: 'blocked', message: result.message ?? 'Akses ekspor ditolak.' };
    default:
      return { kind: 'error', message: result.message ?? `HTTP ${result.status}` };
  }
}

export function useReports(options: UseReportsOptions): UseReportsResult {
  const { token } = options;
  const [form, setForm] = useState<ReportsForm>(EMPTY_REPORTS_FORM);
  const [state, setState] = useState<ReportsState>({ kind: 'idle' });
  const [request, setRequest] = useState<
    { readonly attempt: number; readonly query: ReportsQuery } | null
  >(null);
  const attemptRef = useRef(0);
  const [format, setFormat] = useState<ReportExportFormat>('csv');
  const [purposeCode, setPurposeCode] = useState('');
  const [exportState, setExportState] = useState<ReportExportState>({ kind: 'idle' });
  const [
    exportRequest,
    setExportRequest,
  ] = useState<{
    readonly attempt: number;
    readonly request: { query: ReportsQuery; format: ReportExportFormat; purposeCode: string };
  } | null>(null);

  const fetchRef = useRef<(query: ReportsQuery, token: string) => Promise<ReportsFetchResult>>(
    options.fetch ?? ((q: ReportsQuery, t: string) => api.fetchReports(q, t)),
  );
  fetchRef.current = options.fetch ?? ((q: ReportsQuery, t: string) => api.fetchReports(q, t));
  const fetchExportRef = useRef<
    (
      request: { query: ReportsQuery; format: ReportExportFormat; purposeCode: string },
      token: string,
    ) => Promise<ReportExportResult>
  >(
    options.fetchExport ??
      ((r: { query: ReportsQuery; format: ReportExportFormat; purposeCode: string }, t: string) =>
        api.fetchReportExport(r, t)),
  );
  fetchExportRef.current =
    options.fetchExport ??
    ((r: { query: ReportsQuery; format: ReportExportFormat; purposeCode: string }, t: string) =>
      api.fetchReportExport(r, t));

  const errors = validateReportsForm(form);

  const setField = useCallback((field: keyof ReportsForm, value: string) => {
    setForm((current) => ({ ...current, [field]: value }));
  }, []);

  const submit = useCallback(() => {
    if (hasReportsFormErrors(validateReportsForm(form))) return;
    attemptRef.current += 1;
    setRequest({ attempt: attemptRef.current, query: toReportsQuery(form) });
  }, [form]);

  const nextPage = useCallback(() => {
    setState((current) => {
      if (current.kind !== 'ready' || current.page.nextCursor === null) return current;
      attemptRef.current += 1;
      setRequest({
        attempt: attemptRef.current,
        query: toReportsQuery(form, current.page.nextCursor),
      });
      return current;
    });
  }, [form]);

  const reset = useCallback(() => {
    setForm(EMPTY_REPORTS_FORM);
    setRequest(null);
    setState({ kind: 'idle' });
  }, []);

  useEffect(() => {
    if (request === null) return undefined;
    if (token === null) {
      setState({ kind: 'unauthorized' });
      return undefined;
    }
    let cancelled = false;
    setState({ kind: 'loading' });
    fetchRef.current(request.query, token)
      .then((result) => {
        if (!cancelled) setState(mapReportsResult(result));
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Kesalahan jaringan',
          });
        }
      });
    return () => {
      cancelled = true;
    };
    // `request.query` is a snapshot taken by submit/nextPage: editing a filter
    // after submission never silently re-fetches a different report.
  }, [token, request]);

  const runExport = useCallback(() => {
    if (hasReportsFormErrors(validateReportsForm(form))) return;
    setExportRequest((current) => ({
      attempt: (current?.attempt ?? 0) + 1,
      request: { query: toReportsQuery(form), format, purposeCode },
    }));
  }, [form, format, purposeCode]);

  const resetExport = useCallback(() => {
    setExportRequest(null);
    setExportState({ kind: 'idle' });
  }, []);

  useEffect(() => {
    if (exportRequest === null) return undefined;
    if (token === null) {
      setExportState({ kind: 'unauthorized' });
      return undefined;
    }
    let cancelled = false;
    setExportState({ kind: 'loading' });
    fetchExportRef.current(exportRequest.request, token)
      .then((result) => {
        if (!cancelled) setExportState(mapExportResult(result));
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setExportState({
            kind: 'error',
            message: error instanceof Error ? error.message : 'Kesalahan jaringan',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [token, exportRequest]);

  return {
    form,
    setField,
    errors,
    submit,
    nextPage,
    reset,
    state,
    format,
    setFormat,
    purposeCode,
    setPurposeCode,
    runExport,
    exportState,
    resetExport,
  };
}